import api from './api'

/**
 * The diligence checklist for one deal — what the lender needs from the
 * borrower, and what has actually arrived.
 *
 * The list seeds itself from the standard package and then stays current
 * against the documents themselves, so "outstanding" is answered by what came
 * in rather than by someone remembering to tick a box.
 */
export interface RequirementEvidence {
  filename: string
  /** How the match was made — the parser's document role, or the filename. */
  matchedOn: string
  why: string | null
  source: string | null
  receivedAt: string | null
}

export type RequirementStatus = 'outstanding' | 'received' | 'waived'

export interface DocumentRequirement {
  id: string
  key: string
  label: string
  category: string
  /** Why a lender asks for it. Shown to whoever is chasing it. */
  purpose: string | null
  required: boolean
  addedByHand: boolean
  status: RequirementStatus
  /** What satisfied it. A tick with no file behind it is a claim nobody can check. */
  satisfiedBy: RequirementEvidence[]
  satisfiedAt: string | null
  waivedBy: string | null
  waivedReason: string | null
  isStandard: boolean
}

export interface DocumentChecklist {
  loanId: string
  items: DocumentRequirement[]
  requiredCount: number
  receivedCount: number
  outstandingCount: number
  waivedCount: number
  optionalCount: number
  /** Null when nothing is asked of the deal — unstarted, not complete. */
  completionPct: number | null
}

export const documentRequirements = {
  list: (loanId: string) =>
    api.get(`/loans/${loanId}/document-requirements`).then((r) => r.data as DocumentChecklist),

  add: (loanId: string, body: { label: string; key?: string; category?: string; required?: boolean }) =>
    api.post(`/loans/${loanId}/document-requirements`, body).then((r) => r.data as DocumentChecklist),

  update: (
    loanId: string,
    requirementId: string,
    body: { required?: boolean; status?: RequirementStatus; waivedReason?: string },
  ) =>
    api
      .patch(`/loans/${loanId}/document-requirements/${requirementId}`, body)
      .then((r) => r.data as DocumentChecklist),

  remove: (loanId: string, requirementId: string) =>
    api
      .delete(`/loans/${loanId}/document-requirements/${requirementId}`)
      .then((r) => r.data as DocumentChecklist),
}
